import { useState, useRef, useMemo, useEffect } from "react";
import { Image as ImageIcon, Upload, Camera, Link as LinkIcon, Search, X, Loader2, Plus, CheckSquare, Tag as TagIcon, Trash2, ChevronDown, ChevronUp, SlidersHorizontal } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import DesignCard from "../components/DesignCard";
import DesignLightbox from "@ui/DesignLightbox";
import TagPicker from "@ui/TagPicker";
import useDesignItems from "@features/design/hooks/useDesignItems";
import useHeroImage from "@features/dashboard/hooks/useHeroImage";
import { parseEmbedSnippet } from "@shared/utils/linkPreview";

const SEED_TAGS = [
  "appliance",
  "tile",
  "floor",
  "cabinet",
  "countertop",
  "lighting",
  "plumbing",
  "paint",
  "hardware",
  "dining",
  "office",
  "kitchen",
  "bath",
  "exterior",
  "inspiration",
];

export default function Design() {
  const { items, addPhoto, addLink, updateItem, attachImage, removeItem } = useDesignItems();
  const { setHeroFromUrl } = useHeroImage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [search, setSearch] = useState("");
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total }
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkTagPicker, setShowBulkTagPicker] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Build the union of seed tags + tags currently in use
  const allTags = useMemo(() => {
    const set = new Set(SEED_TAGS);
    items.forEach((it) => (it.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (activeTags.length > 0) {
        const hasAll = activeTags.every((t) => (it.tags || []).includes(t));
        if (!hasAll) return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${it.caption || ""} ${it.title || ""} ${it.sourceUrl || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, activeTags, search]);

  const toggleTag = (tag) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    // Auto-tag with whatever is in the active filter
    const autoTags = [...activeTags];
    setBusy(true);
    setError("");
    setUploadProgress({ current: 0, total: files.length });
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        await addPhoto(files[i], { tags: autoTags });
      } catch (err) {
        console.error(err);
        failed++;
      }
      setUploadProgress({ current: i + 1, total: files.length });
    }
    setBusy(false);
    setUploadProgress(null);
    if (failed > 0) {
      setError(`${failed} of ${files.length} upload${files.length === 1 ? "" : "s"} failed.`);
    }
  };

  const handleFile = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = "";
  };

  // Window-level drag-and-drop for image files
  useEffect(() => {
    const handleDragEnter = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
        dragCounter.current++;
        setDragging(true);
      }
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounter.current--;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        setDragging(false);
      }
    };
    const handleDragOver = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    };
    const handleDrop = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) uploadFiles(files);
    };
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddLink = async () => {
    const input = linkUrl.trim();
    if (!input) return;
    const autoTags = [...activeTags];
    setBusy(true);
    setError("");
    try {
      // First, see if the user pasted an HTML embed snippet (e.g. from Houzz share)
      const embed = parseEmbedSnippet(input);
      let item;
      if (embed) {
        item = await addLink(embed.sourceUrl, {
          imageUrl: embed.imageUrl,
          title: embed.title,
          skipPreview: true,
          tags: autoTags,
        });
      } else {
        item = await addLink(input, { tags: autoTags });
      }
      setLinkUrl("");
      setShowLinkForm(false);
      if (item && !item.imageUrl) {
        setError("Link added, but we couldn't fetch a preview image (the site may block scrapers). Open it in the lightbox to add an image manually.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to add link: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Multi-select helpers ──
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const handleCardClick = (item, index) => {
    if (selectionMode) {
      toggleSelect(item.id);
    } else {
      setLightboxIndex(index);
    }
  };

  const applyBulkTags = (newTags) => {
    // Merge new tags into each selected item (don't overwrite existing)
    selectedIds.forEach((id) => {
      const item = items.find((it) => it.id === id);
      if (!item) return;
      const merged = Array.from(new Set([...(item.tags || []), ...newTags]));
      updateItem(id, { tags: merged });
    });
    setShowBulkTagPicker(false);
    clearSelection();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} item${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    for (const id of selectedIds) {
      const item = items.find((it) => it.id === id);
      if (item) await removeItem(item);
    }
    clearSelection();
  };

  const inputStyle = { background: "var(--bg-input)", border: "1px solid var(--border-input)" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <InfoBentoCard
        cornerGlow
        icon={<ImageIcon className="w-4 h-4" />}
        accent="purple"
        title="Design"
        subtitle={`${items.length} item${items.length === 1 ? "" : "s"} in your design library`}
        className="mb-6"
      />

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Upload Photo
        </button>
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-200 transition-colors disabled:opacity-50"
          style={inputStyle}
        >
          <Camera className="w-3.5 h-3.5" />
          Take Photo
        </button>
        <button
          onClick={() => setShowLinkForm(!showLinkForm)}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-200 transition-colors disabled:opacity-50"
          style={inputStyle}
        >
          <LinkIcon className="w-3.5 h-3.5" />
          Add Link
        </button>
        <button
          onClick={() => {
            if (selectionMode) clearSelection();
            else setSelectionMode(true);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            selectionMode ? "bg-amber-500/20 text-amber-300" : "text-gray-200"
          }`}
          style={selectionMode ? undefined : inputStyle}
        >
          <CheckSquare className="w-3.5 h-3.5" />
          {selectionMode ? "Cancel" : "Select"}
        </button>

        {/* Search */}
        <div className="flex items-center gap-1.5 ml-auto rounded-lg px-2.5 py-1.5" style={inputStyle}>
          <Search className="w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="bg-transparent text-xs text-gray-200 outline-none w-32 md:w-48"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-gray-500 hover:text-gray-300">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Auto-tag hint */}
      {activeTags.length > 0 && (
        <div className="flex items-center gap-2 mb-4 text-[11px] text-gray-500">
          <TagIcon className="w-3 h-3 text-indigo-400" />
          <span>New uploads &amp; links will be auto-tagged with:</span>
          {activeTags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFile}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />

      {/* Link form */}
      {showLinkForm && (
        <InfoBentoCard cornerGlow frostedGradient accent="blue" className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Paste a product link or embed snippet
          </p>
          <div className="flex items-start gap-2">
            <textarea
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com/product/123  — or paste a Houzz/Pinterest embed <div>…</div>"
              rows={2}
              className="flex-1 text-xs text-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              style={inputStyle}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddLink();
                }
              }}
              autoFocus
            />
            <button
              onClick={handleAddLink}
              disabled={busy || !linkUrl.trim()}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Add
            </button>
            <button
              onClick={() => {
                setShowLinkForm(false);
                setLinkUrl("");
              }}
              className="text-gray-500 hover:text-gray-300 px-1 py-1.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-gray-600 mt-2">
            URLs are auto-fetched for a preview image. For sites that block scrapers (Houzz, Pinterest), use their <em>Share → Embed</em> option and paste the HTML snippet here instead.
          </p>
        </InfoBentoCard>
      )}

      {error && (
        <div className="mb-4 p-2 rounded-lg text-xs text-red-400" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}

      {/* Tag filter — collapsible */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
              activeTags.length > 0 ? "bg-indigo-500/25 text-indigo-300" : "text-gray-400"
            }`}
            style={activeTags.length > 0 ? undefined : inputStyle}
          >
            <SlidersHorizontal className="w-3 h-3" />
            Filter{activeTags.length > 0 && ` (${activeTags.length})`}
            {filtersOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {/* Active filter pills (always visible when filters are set) */}
          {activeTags.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/25 text-indigo-300 hover:bg-indigo-500/35 transition-colors"
              title={`Remove ${tag}`}
            >
              {tag}
              <X className="w-2.5 h-2.5" />
            </button>
          ))}
          {activeTags.length > 0 && (
            <button
              onClick={() => setActiveTags([])}
              className="text-[10px] text-gray-500 hover:text-gray-300 px-1"
            >
              Clear all
            </button>
          )}
        </div>
        {filtersOpen && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2" style={{ borderTop: "1px solid var(--border-row)" }}>
            {allTags.map((tag) => {
              const on = activeTags.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                    on
                      ? "bg-indigo-500/30 text-indigo-300"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                  style={on ? undefined : inputStyle}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Gallery grid */}
      {filteredItems.length === 0 ? (
        <InfoBentoCard cornerGlow frostedGradient accent="purple" className="text-center py-12">
          <ImageIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-xs text-gray-500">
            {items.length === 0
              ? "No design items yet. Upload a photo or paste a product link to get started."
              : "No items match your filters."}
          </p>
        </InfoBentoCard>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredItems.map((item, i) => (
            <DesignCard
              key={item.id}
              item={item}
              onClick={() => handleCardClick(item, i)}
              selectionMode={selectionMode}
              selected={selectedIds.has(item.id)}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <DesignLightbox
          items={filteredItems}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onUpdate={updateItem}
          onDelete={removeItem}
          onAttachImage={attachImage}
          onSetCover={setHeroFromUrl}
          allTags={allTags}
        />
      )}

      {/* Sticky bulk action bar */}
      {selectionMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-xl" style={{ background: "rgba(15,17,30,0.98)", border: "1px solid rgba(255,255,255,0.12)" }}>
          <span className="text-xs text-gray-300 font-semibold pr-2 border-r border-white/10">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => setShowBulkTagPicker(true)}
            disabled={selectedIds.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-40"
          >
            <TagIcon className="w-3.5 h-3.5" />
            Tag
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
          <button
            onClick={clearSelection}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-300 hover:text-gray-100 transition-colors"
          >
            Done
          </button>
        </div>
      )}

      {/* Bulk tag picker */}
      {showBulkTagPicker && (
        <TagPicker
          initialTags={[]}
          allTags={allTags}
          title={`Tag ${selectedIds.size} item${selectedIds.size === 1 ? "" : "s"}`}
          subtitle="Selected tags will be added (existing tags kept)."
          onApply={applyBulkTags}
          onClose={() => setShowBulkTagPicker(false)}
        />
      )}

      {/* Drag overlay */}
      {dragging && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center pointer-events-none" style={{ background: "rgba(79, 70, 229, 0.18)", backdropFilter: "blur(2px)" }}>
          <div className="px-8 py-6 rounded-2xl text-center" style={{ background: "rgba(15,17,30,0.95)", border: "2px dashed rgba(129,140,248,0.6)" }}>
            <Upload className="w-10 h-10 text-indigo-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-200">Drop images to add to Design</p>
            <p className="text-[11px] text-gray-500 mt-1">Multiple files supported</p>
          </div>
        </div>
      )}

      {/* Upload progress toast */}
      {uploadProgress && (
        <div className="fixed bottom-4 right-4 z-[95] px-4 py-3 rounded-xl shadow-xl flex items-center gap-3" style={{ background: "rgba(15,17,30,0.95)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
          <div>
            <p className="text-xs font-semibold text-gray-200">Uploading {uploadProgress.current} / {uploadProgress.total}</p>
            <div className="w-40 h-1 rounded-full mt-1.5" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-1 rounded-full bg-indigo-500 transition-all"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
